import { DependentEventFilter, EventFilter } from '@fullstory/proto/fs/api/segment';
import { useCallback, useMemo, useState } from 'react';
import { SignalStudioPreviewSearchProps } from '../contexts';
import { PrimaryValue, ValueType } from '../types';

type Search = Pick<SignalStudioPreviewSearchProps, 'primary' | 'refinements'> & {
  requireFallback: boolean;
};

const defaultPrimary: PrimaryValue = { type: ValueType.Url, value: '' };
const defaultSearch: Search = { primary: defaultPrimary, refinements: [], requireFallback: false };

type SearchReturn = SignalStudioPreviewSearchProps & {
  replace: (
    primary: PrimaryValue | null,
    refinements: DependentEventFilter[],
    requireFallback: boolean,
  ) => void;
  remove: () => void;
};

const extendSearches = (searches: Search[], length: number) => {
  if (searches.length >= length) {
    return searches;
  }
  return [...searches, ...new Array<Search>(length - searches.length).fill(defaultSearch)];
};

export const usePreviewSearch = (
  eventFilters: (EventFilter | null)[],
  fallbackRefinements: DependentEventFilter[][],
  anyChanged?: () => void,
): SearchReturn[] => {
  const [searches, setSearches] = useState<Search[]>([]);

  const onChange = useCallback(
    (searchUpdates: Partial<Search>, index: number) => {
      setSearches(_searches => {
        const searches = extendSearches(_searches, index + 1);
        const existing = searches[index];
        if (!existing) {
          return searches;
        }
        return [
          ...searches.slice(0, index),
          { ...existing, ...searchUpdates },
          ...searches.slice(index + 1),
        ];
      });
      anyChanged?.();
    },
    [setSearches, anyChanged],
  );

  const removeIndex = useCallback(
    (index: number) => {
      setSearches(searches => {
        if (searches.length <= index) {
          return searches;
        }
        return [...searches.slice(0, index), ...searches.slice(index + 1)];
      });
    },
    [setSearches],
  );

  return useMemo(
    () =>
      fallbackRefinements.map((fallbackRefinements, index) => {
        const search = searches[index] ?? defaultSearch;
        return {
          ...search,
          eventFilters,
          fallbackRefinements,
          onPrimaryChange: primary => onChange({ primary, requireFallback: false }, index),
          onRefinementsChange: refinements =>
            onChange({ refinements, requireFallback: false }, index),
          implicitRefinements: search.requireFallback ? fallbackRefinements : undefined,
          replace: (primary, refinements, requireFallback) =>
            onChange({ primary: primary ?? defaultPrimary, refinements, requireFallback }, index),
          remove: () => removeIndex(index),
        };
      }),
    [fallbackRefinements, searches, defaultSearch, eventFilters, onChange, removeIndex],
  );
};
